import { ChevronDown, HelpCircle } from 'lucide-react';
import { useState } from 'react';

/**
 * The three levels an operator sends, in the order a line goes through them.
 * Volumes are the ones actually observed on a 20 Go plan.
 */
const STEPS = [
  {
    label: 'Alerte 80 %',
    trigger: 'avant la fin du forfait',
    example: '16 Go sur 20 Go',
    description:
      'Prévient avant la fin du forfait. La ligne fonctionne normalement, rien n’est restreint.',
    dot: 'bg-amber-400',
    ring: 'ring-amber-100',
    text: 'text-amber-700',
  },
  {
    label: 'Alerte débit réduit',
    trigger: 'à 100 % du forfait',
    example: '20 Go sur 20 Go',
    description:
      'La totalité du forfait est consommée. Le débit est réduit, sans surcoût, et la ligne reste active.',
    dot: 'bg-orange-500',
    ring: 'ring-orange-100',
    text: 'text-orange-700',
  },
  {
    label: 'Alerte blocage',
    trigger: 'au plafond de sécurité',
    example: '10 Go pour un forfait 5 Go',
    description:
      'L’accès data est coupé une fois le plafond de sécurité atteint. Un changement de forfait à la hausse est nécessaire pour réactiver internet.',
    dot: 'bg-red-500',
    ring: 'ring-red-100',
    text: 'text-red-700',
  },
];

/**
 * Cut-off caps published by the operator, per segment and plan size. Purely
 * informational: nothing in the app derives from these values, they are here
 * so a user can look up the cap that applies to a line. The grid follows no
 * formula (a mobile 5 Go plan cuts at 10 Go, a data 5 Go one at 6 Go), hence
 * the explicit table.
 */
const CUT_OFF_CAPS = [
  {
    segment: 'Ligne mobile',
    hint: 'Voix, SMS, MMS et data',
    rows: [
      { plan: '1 Go', cap: '3 Go' },
      { plan: '5 Go', cap: '10 Go' },
      { plan: '20 Go', cap: '25 Go' },
      { plan: '50 Go', cap: '60 Go' },
      { plan: '100 Go', cap: '115 Go' },
      { plan: '150 Go', cap: '175 Go' },
      { plan: '250 Go', cap: '275 Go' },
      { plan: '500 Go', cap: '600 Go' },
    ],
  },
  {
    segment: 'SIM data',
    hint: 'Data seule',
    rows: [
      { plan: '1 Go', cap: '1,2 Go' },
      { plan: '5 Go', cap: '6 Go' },
      { plan: '20 Go', cap: '24 Go' },
      { plan: '50 Go', cap: '55 Go' },
      { plan: '100 Go', cap: '110 Go' },
      { plan: '150 Go', cap: '165 Go' },
      { plan: '250 Go', cap: '275 Go' },
      { plan: '500 Go', cap: '550 Go' },
      { plan: '1 To', cap: '1 100 Go' },
    ],
  },
];

export function ThresholdsExplainer() {
  const [isOpen, setIsOpen] = useState(false);
  const [areCapsOpen, setAreCapsOpen] = useState(false);

  return (
    <div className="rounded-2xl bg-white shadow-sm">
      <button
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className="group flex w-full cursor-pointer items-center gap-2 px-5 py-3 text-left"
      >
        <HelpCircle className="h-4 w-4 shrink-0 text-slate-400 transition-colors group-hover:text-sky-600" />
        <span className="text-sm font-medium text-slate-700">
          Comment fonctionnent les trois seuils ?
        </span>
        <ChevronDown
          className={`ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="px-5 pb-5">
          <p className="text-sm text-slate-500">
            Eiko envoie une alerte à chaque palier franchi par une ligne. Les
            trois se suivent dans cet ordre.
          </p>

          <ol className="mt-4 space-y-4">
            {STEPS.map((step, index) => (
              <li key={step.label} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={`h-2.5 w-2.5 shrink-0 rounded-full ring-4 ${step.dot} ${step.ring}`}
                  />
                  {index < STEPS.length - 1 && (
                    <span className="mt-1 w-px flex-1 bg-slate-200" />
                  )}
                </div>
                <div className="pb-1">
                  <p className="text-sm">
                    <span className={`font-semibold ${step.text}`}>
                      {step.label}
                    </span>
                    <span className="text-slate-500"> {step.trigger}</span>
                  </p>
                  <p className="text-sm text-slate-600 mt-0.5">
                    {step.description}
                  </p>
                  <p className="text-xs text-slate-400 mt-1 tabular-nums">
                    Exemple: {step.example}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          {/* Cut-off caps, one level deeper: reference data, not the point
              of the explainer. */}
          <div className="mt-4 rounded-xl bg-slate-50">
            <button
              onClick={() => setAreCapsOpen((open) => !open)}
              aria-expanded={areCapsOpen}
              className="flex w-full cursor-pointer items-center gap-2 px-4 py-2.5 text-left"
            >
              <span className="text-sm text-slate-600">
                Voir les plafonds de sécurité par forfait
              </span>
              <ChevronDown
                className={`ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${
                  areCapsOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {areCapsOpen && (
              <div className="space-y-4 px-4 pb-4">
                {CUT_OFF_CAPS.map((group) => (
                  <div key={group.segment}>
                    <p className="text-xs font-semibold text-slate-700">
                      {group.segment}
                    </p>
                    <p className="text-xs text-slate-400">{group.hint}</p>
                    <dl className="mt-2 divide-y divide-slate-200/70">
                      {group.rows.map((row) => (
                        <div
                          key={row.plan}
                          className="flex items-baseline justify-between py-1.5 text-sm tabular-nums"
                        >
                          <dt className="text-slate-500">
                            Forfait {row.plan}
                          </dt>
                          <dd className="font-medium text-slate-900">
                            {row.cap}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="mt-4 text-xs text-slate-400">
            Un dépassement data ne coûte rien: le débit est réduit au lieu
            d’être facturé.
          </p>
        </div>
      )}
    </div>
  );
}
